import { useEffect } from 'react';
import { Box, Grid, LinearProgress, Typography, Card, CardContent, Stack, Alert, useTheme, Chip } from '@mui/material';
import MainCard from 'components/ui/Card/MainCard';
import { useGetStatisticsForAllResourcesInSpecifiedProjectMetaStatisticsProjectresourcesPostMutation } from 'store/metadata/metadataApi';
import { formatBytes } from 'utils/format';

interface ResourceStatisticsProps {
    project: string | null | undefined;
}

const ResourceStatistics = ({ project }: ResourceStatisticsProps) => {
    const theme = useTheme();
    const [getStatistics, { data, isLoading, error }] =
        useGetStatisticsForAllResourcesInSpecifiedProjectMetaStatisticsProjectresourcesPostMutation();

    useEffect(() => {
        if (project) {
            getStatistics({ projectResourcesUsageModel: { project } });
        }
    }, [project, getStatistics]);

    const getProgressColor = (percentage: number) => {
        if (percentage >= 90) return 'error';
        if (percentage >= 75) return 'warning';
        return 'success';
    };

    const is403Error = error && 'status' in error && error.status === 403;

    if (!project) {
        return null;
    }

    return (
        <MainCard title='Resource Statistics'>
            {isLoading && <LinearProgress />}
            {is403Error && (
                <Alert severity='warning' sx={{ mb: 2 }}>
                    You don't have permission to view the statistics of this project. Only the project manager and owner
                    can see them.
                </Alert>
            )}
            {error && !is403Error && (
                <Alert severity='error' sx={{ mb: 2 }}>
                    Error loading resource statistics.
                </Alert>
            )}
            {!isLoading && !error && data && data.length === 0 && (
                <Alert severity='info'>No resource statistics available for this project.</Alert>
            )}

            <Grid container spacing={3}>
                {data?.map((resource, index) => (
                    <Grid item xs={12} key={`${resource.resource_name}-${index}`}>
                        <Card variant='outlined' sx={{ borderColor: theme.palette.divider }}>
                            <CardContent>
                                <Typography variant='h4' gutterBottom color='text.primary'>
                                    {resource.resource_name}
                                </Typography>

                                <Grid container spacing={2}>
                                    {resource.locations.map((location, locIndex) => (
                                        <Grid item xs={12} md={6} lg={4} key={`${location.location_name}-${locIndex}`}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    borderRadius: 1,
                                                    bgcolor: theme.palette.background.default,
                                                }}>
                                                <Stack spacing={2}>
                                                    <Stack
                                                        direction='row'
                                                        justifyContent='space-between'
                                                        alignItems='center'>
                                                        <Typography variant='subtitle1' fontWeight='bold'>
                                                            {location.location_name}
                                                        </Typography>
                                                        <Chip
                                                            label={`${location.percentage.toFixed(1)}%`}
                                                            color={getProgressColor(location.percentage)}
                                                            size='small'
                                                            variant='outlined'
                                                        />
                                                    </Stack>

                                                    <Box>
                                                        <Stack direction='row' justifyContent='space-between' mb={0.5}>
                                                            <Typography variant='caption' color='text.secondary'>
                                                                Usage
                                                            </Typography>
                                                            <Typography variant='caption' color='text.primary'>
                                                                {formatBytes(location.usage)} /{' '}
                                                                {formatBytes(location.allocation)}
                                                            </Typography>
                                                        </Stack>
                                                        <LinearProgress
                                                            variant='determinate'
                                                            value={Math.min(location.percentage, 100)}
                                                            color={getProgressColor(location.percentage)}
                                                            sx={{ height: 8, borderRadius: 1 }}
                                                        />
                                                    </Box>

                                                    <Stack direction='row' spacing={2}>
                                                        <Box>
                                                            <Typography
                                                                variant='caption'
                                                                color='text.secondary'
                                                                display='block'>
                                                                Allocated
                                                            </Typography>
                                                            <Typography variant='body2'>
                                                                {formatBytes(location.allocation)}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography
                                                                variant='caption'
                                                                color='text.secondary'
                                                                display='block'>
                                                                Used
                                                            </Typography>
                                                            <Typography variant='body2'>
                                                                {formatBytes(location.usage)}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography
                                                                variant='caption'
                                                                color='text.secondary'
                                                                display='block'>
                                                                Available
                                                            </Typography>
                                                            <Typography variant='body2'>
                                                                {formatBytes(location.available)}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </MainCard>
    );
};

export default ResourceStatistics;
